import { service } from "@ember/service";
import Base from "ember-simple-auth/authenticators/base";
import type { MedicAuthData } from "parog-web/authenticators/base";
import type SemeiaSessionService from "parog-web/services/session";
import ControlTower from "wise-control-tower";

export default class MedicImplicitAuthenticator extends Base {
  @service requestManager;
  @service declare session: SemeiaSessionService;

  _decodeToken(token) {
    return JSON.parse(window.atob(token.split(".")[1]));
  }

  _extractDataFromToken(token): MedicAuthData | undefined {
    try {
      const payload = this._decodeToken(token);
      return {
        id: payload.sub as string,
        uuid: payload.uuid as string,
        // FIXME role is actually not set in the token
        //  See Tokens::MedicAuth.from_medic.
        //
        // FIXME when setting up Sentry context, there will
        //  be missing information, expected by users of ControlTower.
        //  For instance, role, is_super_user are not present.
        role: payload.role as string,
        as: "medic",
        access_token: token
      };
    } catch (e) {
      ControlTower.captureException(e);
      return;
    }
  }

  _isTokenValid(token) {
    try {
      const payload = this._decodeToken(token);
      const expirationTimestamp = payload.exp * 1000;
      const todayTimestamp = new Date().getTime();

      return todayTimestamp < expirationTimestamp;
    } catch (e) {
      ControlTower.captureException(e);
      return false;
    }
  }

  override async authenticate(requestData) {
    // If the medic is already authenticated, keep him logged in
    // with the previous access token if the new one is already expired
    if (
      this.session.isAuthenticated &&
      this.session.data.authenticated.as === "medic" &&
      !this._isTokenValid(requestData.access_token)
    ) {
      return this.session.data.authenticated;
    }

    await this.requestManager.request({
      url: "/api/v1/authorize_medic_token",
      method: "POST",
      body: JSON.stringify(requestData)
    });

    const data = this._extractDataFromToken(requestData.access_token);
    if (!data) return;
    await ControlTower.setUser({ ...data, class: "Medic" });

    this.session.isAuthenticatedAtThisSession = true;
    await this.session.fetchAuthenticatedUser(data);
    return data;
  }

  override async invalidate() {
    await ControlTower.resetUser();
  }

  override async restore(data) {
    if (!this._isTokenValid(data.access_token)) {
      await ControlTower.resetUser();
      throw "Medic Implicit Authenticator - Invalid token";
    }

    await ControlTower.setUser({ ...data, class: "Medic" });

    await this.session.fetchAuthenticatedUser(data);
    return data;
  }
}
